import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { WheelType } from '@sorare/core/src/__generated__/globalTypes';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Grid } from '@sorare/core/src/atoms/layout/grid';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BodyM, BrandM } from '@sorare/core/src/atoms/typography';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { Background } from '#src/components/Wheel/Background/index.tsx';
import { WheelHistory } from '#src/components/Wheel/History/index.tsx';
import { Slot } from '#src/components/Wheel/Slot/index.tsx';

import type {
  WheelOdds,
  WheelOddsVariables,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  position: relative;
  isolation: isolate;
  text-align: center;
  background-color: var(--c-nd-50-opaque);
  min-height: 100%;
  width: 100%;
`;

const Scroll = styled(Vertical)`
  overflow-y: auto;
  height: 100%;
  padding: var(--double-unit);
  z-index: 1;
`;

const Title = styled(Vertical)`
  z-index: 1;
`;

const FlexEnd = styled(Horizontal)`
  justify-content: flex-end;
`;

const query = gql`
  query WheelOdds($sport: Sport!, $wheelType: WheelType!, $rarity: Rarity!) {
    market {
      id
      activeWheelConfig(sport: $sport, rarity: $rarity) {
        slug
        wheelOdds(wheelType: $wheelType) {
          id
          slots {
            probableConfigs {
              id
              ...Slot_probabilisticBundleSlotConfig
            }
          }
        }
      }
    }
  }
  ${Slot.fragments.ProbabilisticBundleSlotConfig}
` as TypedDocumentNode<WheelOdds, WheelOddsVariables>;

type Props = {
  sport: Sport;
  onClose?: () => void;
  wheelType?: WheelType;
  onWheelTypeChange: (wheelType: WheelType) => void;
  rarity: Rarity;
};

export const Odds = ({
  sport,
  onClose,
  onWheelTypeChange,
  wheelType,
  rarity,
}: Props) => {
  const { data } = useQuery(query, {
    variables: {
      sport,
      wheelType: wheelType ?? WheelType.STANDARD,
      rarity,
    },
    skip: !wheelType,
  });

  const wheelOdds = data?.market?.activeWheelConfig?.wheelOdds;

  const probableConfigs = [...(wheelOdds?.slots[0].probableConfigs ?? [])];

  probableConfigs?.sort((a, b) => a.probability - b.probability);

  return (
    <Wrapper>
      <Background golden={wheelType === WheelType.GOLDEN} />
      <Scroll>
        <FlexEnd>
          {!!onClose && <CloseButton size="small" onClose={onClose} />}
        </FlexEnd>
        <Title>
          <BrandM bold color="var(--c-white)">
            <FormattedMessage id="wheel.odds.title" defaultMessage="Rewards" />
          </BrandM>
          <BodyM>
            <FormattedMessage
              id="wheel.odds.description"
              defaultMessage="Spin for a chance to win one of these rewards"
            />
          </BodyM>
        </Title>
        <TabBar
          compact
          value={wheelType}
          variant="secondary"
          fullWidth
          forceMobileLayout
        >
          <Tab
            value={WheelType.STANDARD}
            onClick={() => {
              onWheelTypeChange(WheelType.STANDARD);
            }}
          >
            <FormattedMessage
              id="wheel.odds.tab.regular"
              defaultMessage="Regular"
            />
          </Tab>
          <Tab
            value={WheelType.GOLDEN}
            onClick={() => {
              onWheelTypeChange(WheelType.GOLDEN);
            }}
          >
            <FormattedMessage
              id="wheel.odds.tab.golden"
              defaultMessage="Golden"
            />
          </Tab>
        </TabBar>
        <Vertical gap={4}>
          <Grid>
            {probableConfigs.map((probableConfig, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Slot key={index} config={probableConfig} rarity={rarity} />
            ))}
          </Grid>
          <WheelHistory rarity={rarity} />
        </Vertical>
      </Scroll>
    </Wrapper>
  );
};
